/**
 * "ResizeObserver loop completed with undelivered notifications" is a benign
 * browser warning that fires when a ResizeObserver callback causes a layout
 * change that triggers another resize within the same frame. Many UI libraries
 * (reactstrap tooltips/popovers, react-select, charts) hit this naturally.
 *
 * In production browsers ignore it. In CRA dev, webpack-dev-server's overlay
 * surfaces it as a runtime error.
 *
 * Two-layer defense:
 *  1. Patch ResizeObserver to defer callbacks via requestAnimationFrame — this
 *     prevents the loop notification from being emitted in the first place.
 *  2. Suppress the message at window.error / unhandledrejection / console.error
 *     in case anything slips past (e.g. a callback that ignores rAF batching).
 *
 * Step 1 must run synchronously, before any library captures a reference to
 * window.ResizeObserver, so this file is imported as the very first line of
 * src/index.js.
 */

// ---------- 1. Patch ResizeObserver to defer callbacks via rAF ----------
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver;
  // eslint-disable-next-line no-undef
  window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          // If the observer was disconnected before rAF fires, skip.
          if (!entries || entries.length === 0) return;
          try {
            callback(entries, observer);
          } catch (e) {
            // Don't let a consumer error escape into the rAF queue.
            // eslint-disable-next-line no-console
            console.error(e);
          }
        });
      });
    }
  };
}

// ---------- 2. Suppress any leftover messages in dev ----------
if (process.env.NODE_ENV === 'development') {
  const shouldIgnoreResizeObserverMessage = (message) =>
    typeof message === 'string' &&
    (message.includes('ResizeObserver loop completed with undelivered notifications') ||
      message.includes('ResizeObserver loop limit exceeded'));

  // window.error — capture phase so we run before webpack-dev-server's overlay listener.
  window.addEventListener(
    'error',
    (event) => {
      const msg = event?.message || event?.error?.message;
      if (shouldIgnoreResizeObserverMessage(msg)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true,
  );

  // unhandledrejection — same defense for promise-wrapped variants.
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const msg = event?.reason?.message || (typeof event?.reason === 'string' ? event.reason : '');
      if (shouldIgnoreResizeObserverMessage(msg)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true,
  );

  // console.error — some overlays poll console.error rather than window.error.
  const realConsoleError = console.error;
  console.error = (...args) => {
    const first = args[0];
    if (first != null && shouldIgnoreResizeObserverMessage(String(first))) return;
    realConsoleError.apply(console, args);
  };
}
